import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { convertDateToStringFormat, getPrivateAdminDashboardMissionPath } from '@bloomays-lib/utils.shared';
import { StatusChip, formatEuro } from '@bloomays-lib/ui.shared/datagrid';
import { BusinessType } from '@bloomays-lib/types.shared';
import { MissionRow, marginTone } from '../../helpers/missionsDashboard';
import { HrefLink } from '@bloomays-lib/ui.shared';

export type MissionDetailPanelProps = {
  mission: MissionRow;
  onClose: () => void;
};

const fmtDate = (d: Date | null) => (d ? convertDateToStringFormat(d, 'dd/MM/yyyy') : '—');
const fmtPct = (pct: number) => `${Math.round(pct * 100)} %`;

const echeanceLabel = (
  finInDays: number | null,
): { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' } => {
  if (finInDays === null) return { label: 'Sans date de fin', tone: 'neutral' };
  if (finInDays < 0) return { label: `Terminée depuis ${Math.abs(finInDays)} j`, tone: 'neutral' };
  if (finInDays <= 30) return { label: `Fin dans ${finInDays} j`, tone: 'danger' };
  if (finInDays <= 60) return { label: `Fin dans ${finInDays} j`, tone: 'warning' };
  return { label: `Fin dans ${finInDays} j`, tone: 'success' };
};

const MissionDetailPanel = ({ mission, onClose }: MissionDetailPanelProps) => {
  const { t } = useTranslation(['mission']);
  const echeance = echeanceLabel(mission.finInDays);

  return (
    <Panel>
      <Head>
        <div>
          <Client>{mission.client || '—'}</Client>
          <MissionLabel>{mission.label || '—'}</MissionLabel>
        </div>
        <IconButton aria-label="Fermer" size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Head>

      <ChipRow>
        {mission.type ? (
          <StatusChip tone={mission.type === BusinessType.IT ? 'info' : 'purple'}>{mission.type}</StatusChip>
        ) : null}
        <StatusChip tone={mission.isActive ? 'success' : 'neutral'} dot>
          {mission.isActive ? t('isActive') : 'Terminée'}
        </StatusChip>
        {mission.replacement ? <StatusChip tone="warning">{t('replacement')}</StatusChip> : null}
      </ChipRow>

      <Section>
        <SectionTitle>Acteurs</SectionTitle>
        <Line>
          <Key>{t('bloomerFullName')}</Key>
          <Val>{mission.bloomer || '—'}</Val>
        </Line>
        <Line>
          <Key>{t('bloomerSocietyName')}</Key>
          <Val>{mission.fournisseur || '—'}</Val>
        </Line>
        <Line>
          <Key>{t('clientSocietyName')}</Key>
          <Val>{mission.client || '—'}</Val>
        </Line>
      </Section>

      <Section>
        <SectionTitle>Période &amp; échéance</SectionTitle>
        <Line>
          <Key>{t('beginMission')}</Key>
          <Val>{fmtDate(mission.begin)}</Val>
        </Line>
        <Line>
          <Key>{t('endMission')}</Key>
          <Val>{fmtDate(mission.end)}</Val>
        </Line>
        <Line>
          <Key>Échéance</Key>
          <Val>
            <StatusChip tone={echeance.tone}>{echeance.label}</StatusChip>
          </Val>
        </Line>
      </Section>

      <Section>
        <SectionTitle>Marge</SectionTitle>
        <Line>
          <Key>Marge / jour</Key>
          <Val>{formatEuro(mission.margeDay)}</Val>
        </Line>
        <Line>
          <Key>Marge / mois (est.)</Key>
          <Val>{formatEuro(mission.margeMonth)}</Val>
        </Line>
        <Line>
          <Key>Taux de marge</Key>
          <Val>
            <StatusChip tone={marginTone(mission.margePct)}>{fmtPct(mission.margePct)}</StatusChip>
          </Val>
        </Line>
      </Section>

      <Section>
        <SectionTitle>Facturation (est. {/* DJ */}20 j / mois)</SectionTitle>
        <Line>
          <Key>{t('sale')}</Key>
          <Val>{formatEuro(mission.vente)}</Val>
        </Line>
        <Line>
          <Key>{t('purchase')}</Key>
          <Val>{formatEuro(mission.achat)}</Val>
        </Line>
        <Line>
          <Key>CA mensuel estimé</Key>
          <Val>{formatEuro(mission.caMonth)}</Val>
        </Line>
        <Line>
          <Key>Achat mensuel estimé</Key>
          <Val>{formatEuro(mission.achatMonth)}</Val>
        </Line>
      </Section>

      <HrefLink
        href={getPrivateAdminDashboardMissionPath(mission.recordId)}
        target="_self"
        color="tertiary"
        icon={<OpenInNewIcon fontSize="small" />}
      >
        Voir la mission
      </HrefLink>
    </Panel>
  );
};

export default MissionDetailPanel;
export { MissionDetailPanel };

const Panel = styled('div')(() => ({
  width: 'min(420px, 92vw)',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  padding: '20px 24px 28px',
}));

const Head = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
}));

const Client = styled('div')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: 18,
  fontWeight: 700,
  color: theme.palette.neutral[90],
}));

const MissionLabel = styled('div')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[60],
  marginTop: 2,
}));

const ChipRow = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
}));

const Section = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  paddingTop: 16,
  borderTop: `1px solid ${theme.palette.neutral[15]}`,
}));

const SectionTitle = styled('div')(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[50],
}));

const Line = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
}));

const Key = styled('span')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[60],
}));

const Val = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.neutral[90],
}));
